import { useState, useMemo } from "react";
import { ChevronDown, Download } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Section";
import Reveal from "@/components/Reveal";
import { legalContent } from "@/data/legalContent";
import { AcademyTrainingsclubTerms, useTrainingsclubTerms } from "@/components/trainingsclub/TrainingsclubTerms";

const UI = {
  de: { kicker: "Rechtliches", title: "AGB & Recht", toc: "Inhaltsverzeichnis", expandAll: "Alle ausklappen", collapseAll: "Alle einklappen", download: "Als PDF herunterladen" },
  en: { kicker: "Legal", title: "Terms & Legal", toc: "Table of contents", expandAll: "Expand all", collapseAll: "Collapse all", download: "Download as PDF" },
};

const AGB = () => {
  const { lang } = useLang();
  const data = legalContent[lang] || legalContent.de;
  const ui = UI[lang] || UI.de;
  const sections = data.sections;
  const tcTerms = useTrainingsclubTerms();

  const [open, setOpen] = useState(() => ({ [sections[0].id]: true }));
  const allOpen = useMemo(() => sections.every((s) => open[s.id]), [open, sections]);

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));
  const setAll = (val) => setOpen(Object.fromEntries(sections.map((s) => [s.id, val])));

  const downloadPdf = () => {
    setAll(true);
    setTimeout(() => window.print(), 150);
  };

  const jumpTo = (id) => {
    setOpen((p) => ({ ...p, [id]: true }));
    setTimeout(() => {
      const el = document.getElementById(`sec-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div data-testid="agb-page">
      <PageHeader kicker={ui.kicker} title={ui.title} />

      <section className="pb-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sprung-Navigation */}
        <Reveal direction="up" className="bg-[#0F0F0F] border border-white/10 p-6 md:p-8 mb-8" data-testid="agb-toc">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <p className="font-mono-lab text-xs tracking-[0.25em] text-[#ff0033] uppercase">{ui.toc}</p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={downloadPdf}
                disabled={!tcTerms.terms}
                data-testid="agb-download-pdf"
                className="flex items-center gap-2 font-mono-lab text-xs uppercase tracking-wide text-white bg-[#ff0033] hover:bg-[#cc0029] transition-colors px-3 py-2 no-print"
              >
                <Download size={14} /> {ui.download}
              </button>
              <button
                type="button"
                onClick={() => setAll(!allOpen)}
                data-testid="agb-toggle-all"
                className="font-mono-lab text-xs uppercase tracking-wide text-neutral-400 hover:text-white transition-colors no-print"
              >
                {allOpen ? ui.collapseAll : ui.expandAll}
              </button>
            </div>
          </div>
          <ol className="space-y-2">
            {sections.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => jumpTo(s.id)}
                  data-testid={`agb-toc-link-${s.id}`}
                  className="group flex items-baseline gap-3 text-left w-full"
                >
                  <span className="font-mono-lab text-xs text-[#ff0033] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-neutral-300 group-hover:text-white transition-colors text-sm md:text-base">{s.title}</span>
                </button>
              </li>
            ))}
          </ol>
        </Reveal>

        {/* Aufklappbare Abschnitte */}
        <div className="space-y-5">
          {sections.map((s) => {
            const isOpen = !!open[s.id];
            return (
              <div
                key={s.id}
                id={`sec-${s.id}`}
                data-testid={`agb-section-${s.id}`}
                className="scroll-mt-28 bg-[#0F0F0F] border border-white/10"
              >
                <button
                  type="button"
                  onClick={() => toggle(s.id)}
                  data-testid={`agb-section-header-${s.id}`}
                  className="w-full flex items-center justify-between gap-4 p-6 md:p-7 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl uppercase tracking-tight leading-tight text-white">
                      {s.title}
                    </h2>
                    {s.meta && (
                      <p className="font-mono-lab text-[11px] tracking-wide text-neutral-500 uppercase mt-1.5">{s.meta}</p>
                    )}
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-[#ff0033] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isOpen && (
                  <div className="px-6 md:px-7 pb-8 pt-1 border-t border-white/10 space-y-6">
                    {s.items.map((it, idx) => (
                      <div key={idx} data-testid={`agb-item-${s.id}-${idx}`}>
                        <h3 className="text-white font-semibold text-sm md:text-base mb-2">{it.h}</h3>
                        {s.id === "agb-academy" && it.h.startsWith("§12 ") ? <AcademyTrainingsclubTerms state={tcTerms} /> : <p className="text-neutral-400 whitespace-pre-line leading-relaxed text-sm md:text-[15px]">
                          {it.b}
                        </p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <style>{`
        @media print {
          header, nav, footer, [data-testid="sticky-cta-bar"], .no-print { display: none !important; }
          body, html { background: #fff !important; }
          [data-testid="agb-page"] * { color: #000 !important; background: #fff !important; border-color: #ccc !important; }
          [data-testid="agb-page"] { padding: 0 !important; }
          [data-testid="agb-toc"] { display: none !important; }
          .scroll-mt-28 { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default AGB;
